import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Icon from '../icon';
import Tooltip from '../tooltip';
import cloneDeep from 'lodash/cloneDeep';
import AsyncValidator from 'async-validator';
import { FormContext, FormItemContext } from './form-context';

AsyncValidator.warning = function () {};

const prefixClassName = 'ten-form-item';
const STATUS = {
  ERROR: 'error',
  LOADING: 'loading',
  SUCCESS: 'success',
  WARNING: 'warning',
};
const emptyFn = () => {};

function getPropByPath(obj, path) {
  let tempObj = obj;
  path = path.replace(/\[(\w+)\]/g, '.$1');
  path = path.replace(/^\./, '');

  const keyArr = path.split('.');
  let i = 0;

  for (let len = keyArr.length; i < len - 1; ++i) {
    const key = keyArr[i];
    if (key in tempObj) {
      tempObj = tempObj[key];
    } else {
      throw new Error('prop传值不正确');
    }
  }
  return {
    o: tempObj,
    k: keyArr[i],
    v: tempObj[keyArr[i]],
  };
}

/**
 * @visibleName Form Item
 */
class FormItem extends React.Component {
  static propTypes = {
    /**
     * 标签名
     */
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]),
    /**
     * 标签状态
     */
    status: PropTypes.oneOf(['error', 'loading', 'success', 'warning', '']),
    /**
     * 错误信息
     */
    error: PropTypes.string,
    /**
     * 必填提示
     */
    required: PropTypes.bool,
    /**
     * 是否显示状态icon
     */
    statusIcon: PropTypes.bool,
    /**
     * 提示信息
     */
    tip: PropTypes.string,
    prop: PropTypes.string,
    /**
     * 表单验证规则
     */
    rules: PropTypes.oneOf([PropTypes.object, PropTypes.array]),
    /**
     * 表单域验证错误信息文本
     */
    error: PropTypes.string,
    validateStatus: PropTypes.bool,
    /**
     * 原生 label 的 for 属性
     */
    labelFor: PropTypes.string,
  };

  static defaultProps = {
    statusIcon: true,
  };
  static getDerivedStateFromProps(nextProps, prevState) {
    const validateState = nextProps.status === undefined ? prevState.validateState : nextProps.status;
    const validateMessage = nextProps.error === undefined ? prevState.validateMessage : nextProps.error;

    return { validateState, validateMessage };
  }
  static contextType = FormContext;

  get fieldValue() {
    const { model } = this.context;
    const { prop } = this.props;

    if (!model || !prop) return undefined;

    const path = this.getPath(prop);
    return getPropByPath(model, path).v;
  }

  constructor(props) {
    super(props);
    this.validateDisabled = false;
    this.dataRules = [];
    this.state = {
      validateState: props.status,
      validateMessage: props.error,
    };
  }

  static childContextTypes = {
    formBlur: PropTypes.func,
    formChange: PropTypes.func,
  };

  getChildContext() {
    return {
      formBlur: () => this.onFieldBlur(),
      formChange: () => this.onFieldChange(),
    };
  }

  componentDidMount() {
    if (this.props.prop) {
      const { formItemAdd } = this.context;
      formItemAdd && formItemAdd(this);
      this.updateRules();
    }
  }

  componentWillUnmount() {
    const { formItemRemove } = this.context;
    formItemRemove && formItemRemove(this);
  }

  getPath(prop) {
    let path = prop;
    if (path.indexOf(':') !== -1) {
      path = path.replace(/:/, '.');
    }
    return path;
  }

  /**
   * 重置表单项
   * @public
   */
  resetField() {
    this.clearValidate();

    const { formDefaultModel: defaultModel, model } = this.context;
    let { prop: path } = this.props;

    if (path.indexOf(':') !== -1) {
      path = path.replace(/:/, '.');
    }

    const defaultProp = getPropByPath(defaultModel, path, true);
    const prop = getPropByPath(model, path, true);

    prop.o[prop.k] = cloneDeep(defaultProp.o[prop.k]);
  }

  updateRules() {
    const { rules: selfRules, prop } = this.props;
    let formRules = this.context.rules;

    formRules = formRules ? formRules[prop] : [];
    this.dataRules = [].concat(selfRules || formRules || []);
  }

  /**
   * 清除表单项验证错误
   * @public
   */
  clearValidate() {
    this.validateDisabled = true;
    this.setState({
      validateState: '',
      validateMessage: '',
    });
    setTimeout(() => {
      this.validateDisabled = false;
    }, 300);
  }

  getIcon = () => {
    const { status } = this.props;
    switch (status) {
      case STATUS.ERROR:
        return <Icon type="circle_clear" />;
      case STATUS.SUCCESS:
        return <Icon type="circle_tick" />;
      case STATUS.WARNING:
        return <Icon type="circle_error" />;
      case STATUS.LOADING:
        return <Icon type="loading_gradient" />;
      default:
        return null;
    }
  };

  getFilteredRule(trigger) {
    const rules = this.dataRules;

    if (!trigger) {
      return rules;
    }

    return rules.filter((rule) => {
      let ruleTriggers = rule.trigger;
      if (!ruleTriggers) {
        return true;
      }
      if (!Array.isArray(ruleTriggers)) {
        ruleTriggers = [ruleTriggers];
      }
      return ruleTriggers.indexOf(trigger) > -1;
    });
  }

  /**
   * 验证表单项
   */
  validate(trigger, callback = () => {}) {
    const { prop } = this.props;
    const rules = this.getFilteredRule(trigger);
    if (!rules || rules.length === 0) {
      callback();
      return Promise.resolve('');
    }

    this.validateDisabled = false;
    this.setState({
      validateState: 'validating',
    });

    const descriptor = {};
    descriptor[prop] = rules;

    const validator = new AsyncValidator(descriptor);
    const model = {};

    model[prop] = this.fieldValue;

    const promise = new Promise((resolve, reject) => {
      validator.validate(model, { firstFields: true }, (errors) => {
        this.setState(
          {
            validateState: !errors ? 'success' : 'error',
            validateMessage: errors ? errors[0].message : '',
          },
          () => {
            callback(errors);
            errors ? reject(errors) : resolve('');
          },
        );
      });
    });

    promise.catch(emptyFn);
    return promise;
  }

  onFieldBlur() {
    this.validate('blur');
  }

  onFieldChange() {
    if (this.validateDisabled) {
      return;
    }

    this.validate('change');
  }

  render() {
    const { props } = this;
    const { style, className: propClassName, label, children, required, status, error, statusIcon, tip } = props;
    const { labelWidth, labelAlign } = this.context || {};
    const { validateState, validateMessage } = this.state;

    const _requried = required || this.dataRules.some((rule) => rule.required);

    const className = classNames(propClassName, prefixClassName, {
      [`${prefixClassName}--required`]: _requried,
      [`${prefixClassName}--error`]: validateState === 'error',
      [`${prefixClassName}--loading`]: validateState === 'loading',
      [`${prefixClassName}--success`]: validateState === 'success',
      [`${prefixClassName}--warning`]: validateState === 'warning',
      [`${prefixClassName}--icon`]: statusIcon && (tip || status || error),
    });

    const labelStyles = {
      ...(labelWidth && { width: labelWidth }),
      ...(labelAlign && { textAlign: labelAlign }),
    };

    const providerValue = {
      formBlur: () => this.onFieldBlur(),
      formChange: () => this.onFieldChange(),
    };

    return (
      <FormItemContext.Provider value={providerValue}>
        <div className={className} style={style}>
          {label && (
            <label className={`${prefixClassName}-label`} style={labelStyles}>
              {typeof label === 'function' ? label() : label}
            </label>
          )}

          <div className={`${prefixClassName}-control`}>
            {children}
            <div className={`${prefixClassName}-err`}>{validateMessage}</div>
          </div>
          <div className={`${prefixClassName}-icon`}>
            {tip && !status && (
              <div className={`${prefixClassName}-tip`}>
                <Tooltip message={tip}>
                  <Icon type="line_circle_help" />
                </Tooltip>
              </div>
            )}
            {this.getIcon(status)}
          </div>
        </div>
      </FormItemContext.Provider>
    );
  }
}

export default FormItem;
